import './styles.css';
import {Paper, Grid, Typography, Card, CardMedia, IconButton, Fab} from '@mui/material';
import {useState, useEffect} from 'react';
import { useIsSmallScreen, useIsTinyMobileScreen } from '../../hooks/screenSizeHooks';
import RefreshIcon from '@mui/icons-material/Refresh';
import SentimentSatisfiedAltRoundedIcon from '@mui/icons-material/SentimentSatisfiedAltRounded';
import SentimentVeryDissatisfiedSharpIcon from '@mui/icons-material/SentimentVeryDissatisfiedSharp';
import MusicLoader from '../Loading';
import FlashCardsEndModal from '../../common/FlashCardsEndModal';
import useTimer from '../../hooks/timerHooks';
import {formatTime} from '../../utils';
import { midiPlaySound } from '../../hooks/midiPlaySound';
import { MIDI_VIOLIN_SOUND_CHOICE, MIDI_PIANO_SOUND_CHOICE, MIDI_INCORRECT_SOUND_CHOICE } from '../../utils';
import InvisibleMidiSound from '../InvisibleMidiSound';
import DonationPrompt from '../DonationPrompt';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAnime } from '../../hooks/AnimeProvider';

const MIDI_LENGTH_CORRECT = 0.3;
const MIDI_LENGTH_INCORRECT = 3;

const FlashCardsSession = (props) => {
	const {chosenFlashCardsData, setChosenFlashCardsData, chosenFlashCardsInitNum ,setIsEndModalOpen, aboardSession, isEndModalOpen, pathname} = props;
	const {timer, isActive, handleStart, handlePause} = useTimer(0)
	const {handleTriggerAnime, anime} = useAnime();
	const [dataIndex, setDataIndex] = useState(0);
	const [correctAnswers, setCorrectAnswers] = useState([]);
	const [incorrectAnswers, setIncorrectAnswers] = useState([]);
	const [isLoading, setIsLoading ] = useState(false);
	const [showCorrection, setShowCorrection] = useState(false);
	const [buttonHover, setButtonHover] = useState('');
	const isSmallScreen = useIsSmallScreen();
	const isTinyMobileScreen = useIsTinyMobileScreen();
	const MIDI_CHOSEN_INSTR_SOUND = pathname === '/flashcards/for_piano' ? MIDI_PIANO_SOUND_CHOICE : MIDI_VIOLIN_SOUND_CHOICE;
	const hasLargeView = useMediaQuery('(min-width:1000px)');

	useEffect(() => {
		if(timer === 0 && dataIndex === 0 && !isActive) {
			handleStart();
		}
	}, [timer, dataIndex, isActive, handleStart ])

	const handleNextQuestion = (lastAnwswerIncorrect) => {
		if(dataIndex + 1 < chosenFlashCardsData.length || lastAnwswerIncorrect) {
			setDataIndex(dataIndex + 1);
		} else {
			handlePause();
			setIsEndModalOpen(true);
		}	
	}

	const handleLoadingTime = (lastAnwswerIncorrect) => {
		setIsLoading(true);
		setTimeout(() => {
			setIsLoading(false);
			handleNextQuestion(lastAnwswerIncorrect);
		}
		, 1000);
	}

	const handleOnClick = async(value) => {
		if(value === chosenFlashCardsData[dataIndex].noteBaseName) {
			setCorrectAnswers([...correctAnswers, chosenFlashCardsData[dataIndex]])
			midiPlaySound(chosenFlashCardsData[dataIndex].midiOctave, chosenFlashCardsData[dataIndex].midiNumber, MIDI_CHOSEN_INSTR_SOUND, MIDI_LENGTH_CORRECT )
			handleLoadingTime();
			handleTriggerAnime();
		} else {
			setIncorrectAnswers([...incorrectAnswers, chosenFlashCardsData[dataIndex]])
			setChosenFlashCardsData([...chosenFlashCardsData, chosenFlashCardsData[dataIndex]])
			const lastAnwswerIncorrect = dataIndex + 1 === chosenFlashCardsData.length
			midiPlaySound(4, 4, MIDI_INCORRECT_SOUND_CHOICE, MIDI_LENGTH_INCORRECT )
			setShowCorrection(true);
			setTimeout(() => {
				setShowCorrection(false);
				handleLoadingTime(lastAnwswerIncorrect);
			}, 3000)
		}
		setButtonHover('')
	};

	return (
		<>
		<InvisibleMidiSound soundChoices={[MIDI_INCORRECT_SOUND_CHOICE, MIDI_CHOSEN_INSTR_SOUND]}/>
			<Paper className={hasLargeView
				 ? 'flashCardScoreBar' : 'flashCardScoreBarMobile'} style={{borderRadius: '10px'}}>
				<Fab color="secondary" id="flashCardModalIconButtons" aria-label="refresh" size='small' onClick={aboardSession}>
					<RefreshIcon />
				</Fab>
				<Typography variant={isSmallScreen? 'body1' : 'h5'} className='scoreTextColor' style={{marginTop: '10px'}}>{formatTime(timer, 'digital')}</Typography>
				<Grid container direction="row">
					<Grid item xs={6} className="scoreTitleContainer">
						<SentimentSatisfiedAltRoundedIcon fontSize={isTinyMobileScreen? 'medium' : 'large'} className='correctTextColor'/>
					</Grid>
					<Grid item xs={6} className="scoreTitleContainer"> 
						<SentimentVeryDissatisfiedSharpIcon fontSize={isTinyMobileScreen? 'medium' : 'large'}  className='incorrectTextColor'/>
					</Grid>
				</Grid>
				<Grid container direction="row">
					<Grid item xs={6} className="scoreMarkContainer">
						<Typography variant={isSmallScreen ? 'body2' : 'h6'} className="correctTextColor">{`${correctAnswers.length} of ${chosenFlashCardsData.length}`}</Typography>
					</Grid>
					<Grid item xs={6} className="scoreMarkContainer"> 
						<Typography variant={isSmallScreen ? 'body2' : 'h6'} className="incorrectTextColor">{`${incorrectAnswers.length} of ${chosenFlashCardsData.length}`}</Typography>
					</Grid>
				</Grid>
			</Paper> 
			<Card 
				className={hasLargeView ? 'flashCardSessionCard' : 'flashCardSessionCardMobile'} 
				style={{borderRadius: '10px', background: chosenFlashCardsInitNum < dataIndex + 1 ? 'linear-gradient(170deg, #ff0000, #ffb3b3)' : ' #f5f3ef'}}
			>
				<Grid container>
					<Grid item xs={12} sm={6}>
						{isLoading ?
							<MusicLoader isSmallScreen={isSmallScreen} isFlashCardsFeature={true}/>
							:
							<CardMedia
								sx={{ height: isTinyMobileScreen ? 200 : isSmallScreen ? 250 : 450}}
								image={chosenFlashCardsData[dataIndex].noteImg}
								title='Note'
							/>
						}
					</Grid>

						<Grid  style={{marginTop: 3, marginBottom: 3, backgroundImage: `url(${anime.url})`}} xs={12} sm={6} container item spacing={2} direction="row" justifyContent='center' alignItems='center'>
								{ showCorrection ? 
									<>
										<img 
											src="https://i.giphy.com/media/v1.Y2lkPTc5MGI3NjExZGNraDFyNWlhNW9uZnZkeDJpZGkzOHJ2eXRvZG1lbmpsaWx0c2phMiZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9cw/f4z6vCWrGcMw6xRlOA/giphy.gif" 
											style={{maxHeight: '50px'}}
											alt="Incorrect Warning Gif"
										/>
										<div style={{height: isSmallScreen ? 250 : 450, alignContent: 'center'}}>
											<Typography variant="h6">The correct answer is: <strong className='incorrectTextColor'>{chosenFlashCardsData[dataIndex].noteBaseName}</strong></Typography> 
										</div>
									</>
								:
									['A', 'B', 'C', 'D', 'E', 'F', 'G'].map(note => (
										<Grid item  key={note}>
											<IconButton
												size={isTinyMobileScreen ? 'small' : isSmallScreen ? 'medium' : 'large'}
												style={{
													boxShadow: '0 10px 20px 0 #666666', 
													backgroundColor: chosenFlashCardsInitNum < dataIndex + 1 && buttonHover === note ? ' #004466' :
														chosenFlashCardsInitNum < dataIndex + 1 ? ' #006699' :
														buttonHover === note ? ' #812092' : ' #9c27b0', 
													color: ' #ffffff', 
													width: isTinyMobileScreen ? '35px' : '57px',
													border: 'solid',
												}}
												onClick={() => handleOnClick(note)}
												onMouseEnter={() => setButtonHover(note)}
												onMouseLeave={ () => setButtonHover('')}
												>
												<Typography variant={isTinyMobileScreen ? 'subtitle2' : isSmallScreen ? 'body1' : ''} >{note}</Typography>
												</IconButton>
										</Grid>
									))
								}
						</Grid>
				</Grid>
			</Card>
			<div className='donationPromptCentering'>
				<div >
					<DonationPrompt />
				</div>
			</div>
			<FlashCardsEndModal 
				isEndModalOpen={isEndModalOpen} 
				aboardSession={aboardSession}
				chosenFlashCardsData={chosenFlashCardsData}
				chosenFlashCardsInitNum={chosenFlashCardsInitNum}
				correctAnswers={correctAnswers}
				incorrectAnswers={incorrectAnswers}
				timer={timer}	
			/>
		</>
	)

}

export default FlashCardsSession;